import is from '@sindresorhus/is';
import { flattenErrors } from '@ui/util/errors';
import React, { useEffect } from 'react';
import {
  FieldErrors,
  type FieldValues,
  get,
  type Path,
  type useForm,
} from 'react-hook-form';

export interface TabDefinition {
  key: string;
  label: string;
  Component: React.ComponentType;
  fields: string[];
}

/**
 * Manage form errors in a zod + react-hook-form tabbed form.
 *
 * @remarks
 *
 * When the form is submitted and there are validation errors, this hook
 * identifies which tab contains the first error and switches to that tab.
 * It also focuses the first field with an error within that tab.
 *
 * @returns A mapping of tab keys to arrays of field errors.
 */
export function useTabbedFormErrors<
  TabKeys extends string,
  FormData extends FieldValues,
>(
  form: ReturnType<typeof useForm<FormData>>,
  tabs: readonly TabDefinition[],
  _currentTab: TabKeys,
  setCurrentTab: (tab: TabKeys) => void,
) {
  const { errors, isSubmitted } = form.formState;

  const errorsByTab: Record<string, string[]> = {};

  for (const tab of tabs) {
    errorsByTab[tab.key] = [];

    for (const field of tab.fields) {
      const fieldErrors = get(errors, field) as FieldErrors<FormData>;

      if (!fieldErrors) {
        continue;
      }

      if (Array.isArray(fieldErrors)) {
        const flatErrors = flattenErrors(fieldErrors);

        errorsByTab[tab.key].push(...Object.keys(flatErrors));
      } else {
        errorsByTab[tab.key].push(field);
      }
    }
  }

  /**
   * Whenever the form is submitted or the errors state changes, change tabs
   * and focus the first errored field in that tab, if necessary.
   */
  useEffect(() => {
    if (!isSubmitted) {
      return;
    }

    const errorsByFieldPath = flattenErrors(errors);

    if (!is.emptyObject(errorsByFieldPath)) {
      for (const tab of tabs) {
        for (const field of tab.fields) {
          const tabFieldWithError = Object.keys(errorsByFieldPath).find(
            (errorField) =>
              errorField === field || errorField.startsWith(field + '.'),
          );

          if (tabFieldWithError) {
            setCurrentTab(tab.key as TabKeys);

            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                form.setFocus(tabFieldWithError as Path<FormData>);
              });
            });

            return;
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitted, errors, tabs, setCurrentTab]);

  return errorsByTab;
}
